import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BASE_API_URL } from '@cnt-workspace/data-access';
import { IActivityLocation, IActivityLocationSearchRequest, ICountResponse, IPhoto } from '@curbnturf/entities';
import { Observable } from 'rxjs';

const API_URL = BASE_API_URL + 'activity-location';

/**
 * POI (activity-location) admin calls: search with approval/hidden/deleted
 * filters, create/update (an admin update may set `available` = approved),
 * delete/restore, images. Replaces @curbnturf/activity's ActivityService +
 * NgRx facade for admin. Same endpoints cnt-landing's ActivityApiService uses.
 */
@Injectable({ providedIn: 'root' })
export class AdminPoiApiService {
  private readonly http = inject(HttpClient);

  search(query: IActivityLocationSearchRequest): Observable<IActivityLocation[]> {
    return this.http.get<IActivityLocation[]>(`${API_URL}?${poiQueryString(query)}`);
  }

  /** POIs awaiting approval: not available, not deleted. */
  pendingCount(): Observable<ICountResponse> {
    return this.http.get<ICountResponse>(`${API_URL}/pending/count`);
  }

  fetch(id: number): Observable<IActivityLocation> {
    return this.http.get<IActivityLocation>(`${API_URL}/${id}`);
  }

  create(poi: IActivityLocation): Observable<IActivityLocation> {
    return this.http.post<IActivityLocation>(API_URL, poi);
  }

  update(poi: IActivityLocation): Observable<IActivityLocation> {
    if (!poi.id) {
      throw new Error('Missing POI Identifier');
    }
    return this.http.put<IActivityLocation>(`${API_URL}/${poi.id}`, poi);
  }

  remove(id: number): Observable<IActivityLocation> {
    return this.http.delete<IActivityLocation>(`${API_URL}/${id}`);
  }

  restore(id: number): Observable<IActivityLocation> {
    return this.http.post<IActivityLocation>(`${API_URL}/${id}/restore`, null);
  }

  uploadImage(id: number, formData: FormData): Observable<{ activityId: number; photos: IPhoto[] }> {
    return this.http.post<{ activityId: number; photos: IPhoto[] }>(`${API_URL}/${id}/upload-image`, formData);
  }

  removeImage(id: number, photoIndex: number): Observable<IPhoto[]> {
    return this.http.delete<IPhoto[]>(`${API_URL}/${id}/remove-image/${photoIndex}`);
  }
}

/** Mirrors the server's `GET /activity-location` query names (selectedDump, activities=a,b, …). */
export function poiQueryString(query: IActivityLocationSearchRequest): string {
  const params = new URLSearchParams();
  const selectedKeys: Record<string, string> = {
    dump: 'selectedDump',
    restArea: 'selectedRestArea',
    wifi: 'selectedWifi',
    potableWater: 'selectedPotableWater',
    propane: 'selectedPropane',
  };
  for (const [key, param] of Object.entries(selectedKeys)) {
    const value = query.selected?.[key as keyof typeof query.selected];
    if (value) params.set(param, String(value));
  }
  if (query.activities?.length) params.set('activities', query.activities.join(','));
  if (query.userId) params.set('userId', String(query.userId));
  for (const key of ['available', 'hidden', 'deleted'] as const) {
    if (typeof query[key] === 'boolean') params.set(key, String(query[key]));
  }
  return params.toString();
}
